// filepath: apps/web/src/features/lists/EntityListRoute.tsx
//
// FR-UI-LIST-1 — route shell for the definition-driven list view.
//
// URL: /lists/:entityType
//
// Reads :entityType from `window.location.pathname` (mirror of the
// PermissionsMatrixRoute / TasksRoute pattern — no react-router
// dependency required, consistent with App.tsx convention).
//
// The route defers entity-definition fetching to the page component
// via the `definitionFetcher` prop. In production the route is
// mounted by AuthStateRouter; in tests it can be mounted directly
// with a custom fetcher.

import React from "react";
import { EntityListPage } from "./EntityListPage";
import type { EntityDefinitionWire } from "./types";

const ENTITY_TYPE_PATH_RE = /^\/lists\/([^/]+)\/?$/;

export interface EntityListRouteProps {
  /**
   * Explicit entity type override. When supplied, the route uses it
   * directly instead of parsing the URL. Useful for tests.
   */
  entityTypeOverride?: string;
}

/**
 * Extract :entityType from the current URL. Returns `null` when
 * the URL does not match the expected pattern.
 */
function extractEntityType(pathname: string): string | null {
  if (typeof window === "undefined") return null;
  const m = pathname.match(ENTITY_TYPE_PATH_RE);
  if (!m || !m[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

/**
 * Default definition fetcher. Hits GET /api/entity-definitions/{entityType}
 * — the FR-UI-CORE-3 endpoint, already used by useDefinition with
 * `version: "active"`. The list view always fetches the active
 * version (ARCHITECTURE_WEB.md §4 — admin / builder / list surfaces).
 */
async function defaultDefinitionFetcher(
  entityType: string,
): Promise<EntityDefinitionWire> {
  const url = `/api/entity-definitions/${encodeURIComponent(entityType)}`;
  const response = await apiFetch(url, {
    init: { headers: { Accept: "application/json" } },
  });
  if (!response.ok) {
    throw new ApiError(
      response.statusText,
      response.status,
      `http_${response.status}`,
    );
  }
  return (await response.json()) as EntityDefinitionWire;
}

import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";

/**
 * EntityListRoute — top-level route shell. Mounts EntityListPage.
 */
export function EntityListRoute({
  entityTypeOverride,
}: EntityListRouteProps): React.ReactElement | null {
  const entityType =
    entityTypeOverride ??
    (typeof window !== "undefined"
      ? extractEntityType(window.location.pathname)
      : null);

  if (entityType === null || entityType === "") {
    return (
      <main data-testid="entity-list-route-no-entity-type">
        <p>Missing entity type.</p>
      </main>
    );
  }

  return (
    <EntityListPage
      entityType={entityType}
      definitionFetcher={defaultDefinitionFetcher}
    />
  );
}
